import { memo, useMemo } from 'react';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Spinner
} from 'design-system';

import type { TableWidgetData } from '@/features/_shared/widget-registry';
import { useMarketPreview } from '@/features/markets/api';
import MarketsComponents from '@/features/markets/components';
import type { MarketsPageType } from '@/features/markets/types';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';

import { buildTemplate } from './tablePreviewTemplate';

export const MarketPreviewShell = ({
  previewData,
  isError,
  isLoading
}: {
  previewData: MarketsPageType | null | undefined;
  isError: boolean;
  isLoading: boolean;
}) => {
  let content;

  if (isLoading) {
    content = (
      <div className='flex justify-center py-6'>
        <Spinner
          aria-label='Loading market preview'
          className='size-6'
        />
      </div>
    );
  } else if (isError && !previewData) {
    content = (
      <Alert>
        <AlertTitle>Market preview unavailable.</AlertTitle>
        <AlertDescription>Try again shortly.</AlertDescription>
      </Alert>
    );
  } else if (previewData) {
    content = (
      <div className='space-y-3'>
        {isError && (
          <Alert>
            <AlertTitle>Some preview data may be unavailable.</AlertTitle>
            <AlertDescription>Available market widgets are shown below.</AlertDescription>
          </Alert>
        )}
        <MarketsComponents
          forChat
          showAddToDashboard
          components={previewData.components}
          showShareBlock={false}
        />
      </div>
    );
  } else {
    content = (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>Preview unavailable.</EmptyTitle>
          <EmptyDescription>No market preview data was returned.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <PageInformationContext value={{ mainPageTitle: 'AI Chat' }}>{content}</PageInformationContext>
  );
};

export const MarketPreviewWidget = memo(({ data }: { data: TableWidgetData }) => {
  const template = useMemo(() => buildTemplate(data), [data]);
  const { data: previewData, isError, isLoading } = useMarketPreview(template);
  return (
    <MarketPreviewShell
      previewData={previewData}
      isError={isError}
      isLoading={isLoading}
    />
  );
});

MarketPreviewWidget.displayName = 'MarketPreviewWidget';
